import { observable, action, computed } from 'mobx';
import tradingVenuesAdapter from 'adapters/TradingVenuesAdapter';
import { TradingVenuesCollection } from './TradingVenuesCollection';

const registerCheckedMap = {
  isRealOrders: {
    oppositeField: 'isDelayedOrders',
    oppositeDisabledField: 'isDelayedOrdersDisabled'
  },
  isRealTraders: {
    oppositeField: 'isDelayedTraders',
    oppositeDisabledField: 'isDelayedTradersDisabled'
  },
  isDelayedOrders: {
    oppositeField: 'isRealOrders',
    oppositeDisabledField: 'isRealOrdersDisabled'
  },
  isDelayedTraders: {
    oppositeField: 'isRealTraders',
    oppositeDisabledField: 'isRealTradersDisabled'
  }
};

class TradingVenuesStore {
  @observable tradingVenuesMap = new TradingVenuesCollection();

  @observable uncompletedPayments = [];
  @observable isVenuesLoadInProgress = false;
  @observable realtimeOrderCost = null;
  @observable realtimeTradeCost = null;
  @observable subscriptionPrice = null;
  @observable subscriptionEndDate = null;
  @observable subscriptionUpdateInProgress = false;
  @observable isUpdated = false;

  subscriptionChangeHandlers = new Map();

  @computed
  get venues() {
    return this.tradingVenuesMap.values();
  }

  @computed
  get isAnySelected() {
    return this.venues.some((venue) => this.isVenueSelected(venue));
  }

  isVenueSelected(venue) {
    return venue.isRealTraders || venue.isRealOrders || venue.isDelayedOrders || venue.isDelayedTraders;
  }

  get registerVenues() {
    return this.venues.filter((venue) => this.isVenueSelected(venue)).map((venue) => {
      return {
        venueId: venue.id,
        isRealTraders: venue.isRealTraders,
        isRealOrders: venue.isRealOrders,
        isDelayedOrders: venue.isDelayedOrders,
        isDelayedTraders: venue.isDelayedTraders
      };
    });
  }

  @action
  onChangeVenueState(item) {
    const venue = this.tradingVenuesMap.get(item.id);
    venue.isTrading = !venue.isTrading;
  }

  @action
  onChangeRegisterType(item, isChecked, fieldName) {
    const venue = this.tradingVenuesMap.get(item.id);
    venue[fieldName] = isChecked;
    venue[registerCheckedMap[fieldName]['oppositeDisabledField']] = isChecked;

    this.subscriptionChangeHandlers.forEach((handler) => handler());
  }

  @action
  LoadTadingVenues() {
    this.isVenuesLoadInProgress = true;
    return tradingVenuesAdapter
      .loadTradingVenues()
      .then(
        action((venues) => {
          this.tradingVenuesMap.clear();

          this.realtimeTradeCost = this.realtimeTradeCost || venues.realtimeTradeCost;
          this.realtimeOrderCost = this.realtimeOrderCost || venues.realtimeOrderCost;

          venues.forEach((item, index) => {
            if (item.id) {
              this.tradingVenuesMap.set(item.id, item);
            }
          });
          this.isVenuesLoadInProgress = false;

          return venues;
        })
      )
      .catch(
        action(() => {
          this.isVenuesLoadInProgress = false;
        })
      );
  }

  cancel(orderId) {
    return tradingVenuesAdapter.cancelPayment(orderId);
  }

  @action
  LoadSubscriptionEndDate() {
    return tradingVenuesAdapter.loadSubscriptions().then(({ subscriptions }) => {
      const objectToIterate = subscriptions || {};
      const subs = Object.keys(objectToIterate).map(function(key) { return objectToIterate[key]; });
      if (subs.length) {
        this.subscriptionEndDate = subs[0].subscriptionEndDate;
      }
    });
  }

  @action
  LoadSubscriptions() {
    this.subscriptionUpdateInProgress = true;
    this.subscriptionPrice = null;

    const getVenueDisabledSatatus = (v) => {
      return {
        isDelayedOrdersDisabled: !!v.isRealOrders,
        isDelayedTradersDisabled: !!v.isRealTraders,
        isRealOrdersDisabled: !!v.isDelayedOrders,
        isRealTradersDisabled: !!v.isDelayedTraders
      };
    };

    return Promise.all([ this.LoadTadingVenues(), tradingVenuesAdapter.loadSubscriptions() ])
      .then(([ venues, { subscriptions, uncompletedPayments } ]) => {
        this.uncompletedPayments = uncompletedPayments || [];

        this.tradingVenuesMap.values().forEach((v) => {
          if (v.id in subscriptions) {
            Object.assign(v, subscriptions[v.id], getVenueDisabledSatatus(subscriptions[v.id]));

            if (!this.subscriptionEndDate) {
              this.subscriptionEndDate = v.subscriptionEndDate;
            }
          }
        });

        this.subscriptionUpdateInProgress = false;

        return { venues, uncompletedPayments };
      })
      .then((result) => {
        this.tradingVenuesMap.commit();
        return result;
      });
  }

  @action
  updateCurrentSubscriptionPrice() {
    this.subscriptionPrice = null;

    return tradingVenuesAdapter.loadSubscriptionPrice(this.registerVenues).then((result) => {
      this.isUpdated = !this.tradingVenuesMap.isDirty();
      this.subscriptionPrice = result.price;
    });
  }

  @action
  updateSubsriptions() {
    this.subscriptionUpdateInProgress = true;
    return tradingVenuesAdapter.updateSubsriptions(this.registerVenues).then((result) => {
      this.subscriptionUpdateInProgress = false;

      return result;
    });
  }

  addSubscriptionsChangeHandler(handlerName, handler) {
    this.subscriptionChangeHandlers.set(handlerName, handler);
  }

  removeSubscriptionsChangeHandler(handlerName) {
    this.subscriptionChangeHandlers.delete(handlerName);
  }

  @action
  SetReadOnlyDelayedMode() {
    this.tradingVenuesMap.values().forEach((v) =>
      Object.assign(v, {
        isRealOrders: false,
        isRealTraders: false,
        isDelayedOrders: v.hasOrders,
        isDelayedTraders: v.hasTrades,
        isDelayedOrdersDisabled: true,
        isDelayedTradersDisabled: true,
        // the following two are optional, but we switch them off just for the sake of consistency
        isRealOrdersDisabled: true,
        isRealTradersDisabled: true
      })
    );
  }
}

const tradingVenuesStore = new TradingVenuesStore();

export default tradingVenuesStore;



// WEBPACK FOOTER //
// ./src/stores/TradingVenuesStore.js